import React, { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, X,
  Type, Image as ImageIcon, Film, Code2, Megaphone, Images, Trophy, Award, Layers, MapPin, Star, Calendar, Heart, Book, Handshake, RotateCcw, Layout,
  Quote, List, Columns, ChevronDown, Minus, MoveVertical, Settings, GripVertical, Mail, Archive, Users, FileText, Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../api/client';
import { BLOCK_CATALOG, DEFAULT_LAYOUTS, PAGES_FOR_LAYOUT } from '../../data/defaultLayouts';
import UploadField from './UploadField';
import RichTextEditor from './RichTextEditor';

const ICON_MAP = { Type, Image: ImageIcon, Film, Code2, Megaphone, Images, Trophy, Award, Layers, MapPin, Star, Calendar, Heart, Book, Handshake, Layout, Quote, List, Columns, ChevronDown, Minus, MoveVertical, Mail, Archive, Users, FileText, Camera };
const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;
const uid = () => 'b' + Math.random().toString(36).slice(2, 9);

const ICON_OPTIONS = ['Star', 'Heart', 'Trophy', 'Flag', 'Shield', 'Target', 'Zap', 'Users', 'Award', 'Calendar', 'MapPin', 'Camera', 'Book', 'Handshake', 'Quote'];

export default function LayoutEditor() {
  const [activePage, setActivePage] = useState(PAGES_FOR_LAYOUT[0].slug);
  return (
    <div className="space-y-4" data-testid="layout-editor">
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Layout size={18} className="text-pink-brand" />
          <h2 className="text-white font-bold text-lg">Layout pagine (drag & drop)</h2>
        </div>
        <p className="text-white/60 text-xs mb-3">
          Trascina i blocchi per riordinarli. Clicca <strong>+</strong> per inserire un blocco tra due esistenti. Clicca <Settings size={11} className="inline" /> per modificare stili (sfondo, colore, padding, allineamento, visibilità).
        </p>
        <div className="flex flex-wrap gap-2">
          {PAGES_FOR_LAYOUT.map((p) => (
            <button key={p.slug} onClick={() => setActivePage(p.slug)}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase border transition-colors ${activePage === p.slug ? 'bg-pink-brand text-white border-pink-brand' : 'bg-navy-dark text-white/70 border-white/15 hover:text-white'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </Card>
      <PageLayoutEditor key={activePage} slug={activePage} />
    </div>
  );
}

function PageLayoutEditor({ slug }) {
  const [blocks, setBlocks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [insertAt, setInsertAt] = useState(null);
  const [openStyleFor, setOpenStyleFor] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api.getLayout(slug);
        if (!alive) return;
        if (Array.isArray(d?.blocks) && d.blocks.length > 0) setBlocks(d.blocks);
        else setBlocks((DEFAULT_LAYOUTS[slug] || []).map((b) => ({ ...b })));
      } catch {
        if (alive) setBlocks((DEFAULT_LAYOUTS[slug] || []).map((b) => ({ ...b })));
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (!blocks) return <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

  const update = (id, patch) => setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => { if (window.confirm('Eliminare questo blocco?')) setBlocks((bs) => bs.filter((b) => b.id !== id)); };
  const move = (id, dir) => {
    setBlocks((bs) => {
      const i = bs.findIndex((b) => b.id === id); const j = i + dir;
      if (j < 0 || j >= bs.length) return bs;
      return arrayMove(bs, i, j);
    });
  };
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setBlocks((bs) => {
      const oldIdx = bs.findIndex((b) => b.id === active.id);
      const newIdx = bs.findIndex((b) => b.id === over.id);
      return arrayMove(bs, oldIdx, newIdx);
    });
  };
  const addBlock = (type, atIdx) => {
    const newB = { id: uid(), type, visible: true };
    if (type === 'text' || type === 'paragraph') newB.content = 'Scrivi qui…';
    else if (type === 'heading') { newB.text = 'Nuovo titolo'; newB.level = 2; }
    else if (type === 'button') { newB.label = 'Scopri'; newB.url = '/'; }
    else if (type === 'banner') Object.assign(newB, { title: 'Titolo banner', subtitle: '', cta_label: '', cta_url: '' });
    else if (type === 'iframe') newB.height = 480;
    else if (type === 'gallery' || type === 'gallery_carousel') newB.items = [];
    else if (type === 'list') newB.items = ['Primo punto', 'Secondo punto'];
    else if (type === 'quote') { newB.text = 'Una citazione memorabile.'; newB.author = ''; }
    else if (type === 'card_group') newB.cards = [{ icon: 'Star', title: 'Card 1', text: 'Descrizione' }];
    else if (type === 'columns') { newB.columns = 2; newB.contents = ['Colonna 1', 'Colonna 2']; }
    else if (type === 'accordion') newB.items = [{ title: 'Domanda', content: 'Risposta' }];
    else if (type === 'spacer') newB.height = 40;
    else if (type === 'html') newB.code = '<p>HTML personalizzato</p>';
    const next = [...blocks];
    const at = atIdx == null ? next.length : Math.max(0, Math.min(atIdx, next.length));
    next.splice(at, 0, newB); setBlocks(next);
    setShowAdd(false); setInsertAt(null);
  };
  const reset = () => { if (window.confirm('Ripristinare il layout di default?')) setBlocks((DEFAULT_LAYOUTS[slug] || []).map((b) => ({ ...b }))); };
  const save = async () => {
    setSaving(true);
    try { await api.saveLayout(slug, blocks); toast.success('Layout salvato'); }
    catch (e) { toast.error(e?.message || 'Errore'); }
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-white/70 text-sm"><strong>{blocks.length}</strong> blocchi · <strong>{blocks.filter((b) => b.visible !== false).length}</strong> visibili</div>
        <div className="flex gap-2">
          <button onClick={reset} className="px-3 py-2 rounded-full bg-navy-dark border border-white/15 text-white/70 text-xs font-bold uppercase inline-flex items-center gap-1"><RotateCcw size={12} /> Default</button>
          <button onClick={() => { setShowAdd(true); setInsertAt(null); }} className="px-3 py-2 rounded-full bg-navy-dark border border-pink-brand text-pink-brand text-xs font-bold uppercase inline-flex items-center gap-1"><Plus size={12} /> Aggiungi blocco</button>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 !text-sm"><Save size={14} /> {saving ? 'Salvo…' : 'Salva layout'}</button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {blocks.map((b, i) => (
              <SortableBlock
                key={b.id} block={b} index={i} total={blocks.length}
                onChange={(patch) => update(b.id, patch)}
                onMove={(dir) => move(b.id, dir)}
                onRemove={() => remove(b.id)}
                onInsertAfter={() => { setShowAdd(true); setInsertAt(i + 1); }}
                onOpenStyle={() => setOpenStyleFor(b.id)}
              />
            ))}
            {blocks.length === 0 && <Card><p className="text-white/60 text-sm text-center py-6">Layout vuoto. Clicca "Aggiungi blocco".</p></Card>}
          </div>
        </SortableContext>
      </DndContext>

      <button onClick={save} disabled={saving} className="btn-primary"><Save size={14} /> {saving ? 'Salvo…' : 'Salva layout'}</button>

      {showAdd && <AddBlockModal onPick={(t) => addBlock(t, insertAt)} onClose={() => { setShowAdd(false); setInsertAt(null); }} insertAt={insertAt} />}
      {openStyleFor && (() => {
        const b = blocks.find((x) => x.id === openStyleFor);
        if (!b) return null;
        return <StylePanel block={b} onChange={(patch) => update(b.id, patch)} onClose={() => setOpenStyleFor(null)} />;
      })()}
    </div>
  );
}

function SortableBlock({ block, index, total, onChange, onMove, onRemove, onInsertAfter, onOpenStyle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const meta = BLOCK_CATALOG.find((c) => c.type === block.type) || { label: block.type, category: 'unknown', icon: 'Type' };
  const Icon = ICON_MAP[meta.icon] || Type;
  const visible = block.visible !== false;
  return (
    <div ref={setNodeRef} style={style} className={`bg-navy-dark border rounded-xl p-3 ${visible ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1.5 text-white/60 hover:text-pink-brand" title="Trascina"><GripVertical size={16} /></button>
        <Icon size={18} className="text-pink-brand flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">{meta.label} <span className="text-white/40 text-xs">#{index + 1}</span></div>
          <div className="text-white/50 text-xs truncate">{meta.desc}</div>
        </div>
        {meta.category === 'system' && <span className="text-[9px] uppercase font-bold bg-pink-brand/20 text-pink-brand px-2 py-0.5 rounded">system</span>}
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => onChange({ visible: !visible })} className={`p-2 rounded ${visible ? 'text-white/70 hover:text-pink-brand' : 'text-pink-brand'}`} title={visible ? 'Nascondi' : 'Mostra'}>
            {visible ? <Eye size={15} /> : <EyeOff size={15} />}
          </button>
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-2 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowUp size={15} /></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-2 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowDown size={15} /></button>
          <button onClick={onOpenStyle} className="p-2 text-white/60 hover:text-pink-brand" title="Stile"><Settings size={15} /></button>
          <button onClick={onInsertAfter} className="p-2 text-white/60 hover:text-pink-brand" title="Inserisci sotto"><Plus size={15} /></button>
          <button onClick={onRemove} className="p-2 text-white/60 hover:text-red-400"><Trash2 size={15} /></button>
        </div>
      </div>
      {meta.category === 'content' && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <BlockFields block={block} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

function BlockFields({ block, onChange }) {
  const t = block.type;
  if (t === 'text' || t === 'paragraph') {
    return (
      <div className="space-y-2">
        <RichTextEditor
          value={block.content || ''}
          onChange={(html) => onChange({ content: html, html: true })}
          placeholder="Scrivi qui il testo… (puoi formattare e inserire immagini)"
          folder="rich"
        />
        <p className="text-white/40 text-[10px]">Suggerimento: usa la barra strumenti per formattare. Le immagini si possono caricare dal PC e si adattano da sole.</p>
      </div>
    );
  }
  if (t === 'heading') {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <input className={inp + ' col-span-3'} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} placeholder="Testo del titolo" />
          <select className={inp} value={block.level || 2} onChange={(e) => onChange({ level: parseInt(e.target.value, 10) })}>
            <option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option><option value={4}>H4</option>
          </select>
        </div>
        <label className="text-white/70 text-xs flex items-center gap-2"><input type="checkbox" checked={block.underline !== false} onChange={(e) => onChange({ underline: e.target.checked })} /> Mostra riga rosa sotto</label>
      </div>
    );
  }
  if (t === 'button') {
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        <input className={inp} value={block.label || ''} onChange={(e) => onChange({ label: e.target.value })} placeholder="Testo pulsante" />
        <input className={inp} value={block.url || ''} onChange={(e) => onChange({ url: e.target.value })} placeholder="/squadre o https://..." />
      </div>
    );
  }
  if (t === 'quote') {
    return (
      <div className="space-y-2">
        <textarea rows={3} className={inp} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })} placeholder="Citazione" />
        <input className={inp} value={block.author || ''} onChange={(e) => onChange({ author: e.target.value })} placeholder="Autore (opzionale)" />
      </div>
    );
  }
  if (t === 'list') {
    return (
      <div className="space-y-2">
        <label className="text-white/70 text-xs flex items-center gap-2"><input type="checkbox" checked={!!block.ordered} onChange={(e) => onChange({ ordered: e.target.checked })} /> Numerato (1, 2, 3…)</label>
        <textarea rows={5} className={inp} value={(block.items || []).join('\n')} onChange={(e) => onChange({ items: e.target.value.split('\n') })} placeholder="Un elemento per riga" />
      </div>
    );
  }
  if (t === 'card_group') {
    const cards = block.cards || [];
    return (
      <div className="space-y-2">
        <div className="grid sm:grid-cols-3 gap-2">
          <input className={inp} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} placeholder="Titolo sezione (opzionale)" />
          <select className={inp} value={block.columns || 3} onChange={(e) => onChange({ columns: parseInt(e.target.value, 10) })}>
            <option value={2}>2 colonne</option><option value={3}>3 colonne</option><option value={4}>4 colonne</option>
          </select>
          <button onClick={() => onChange({ cards: [...cards, { icon: 'Star', title: 'Nuova card', text: '' }] })} className="bg-navy-dark border border-pink-brand text-pink-brand rounded-lg text-xs font-bold uppercase"><Plus size={12} className="inline" /> Card</button>
        </div>
        <div className="space-y-2">
          {cards.map((c, i) => (
            <div key={i} className="bg-navy border border-white/10 rounded p-2 grid sm:grid-cols-12 gap-2 items-center">
              <select className={inp + ' sm:col-span-2'} value={c.icon || 'Star'} onChange={(e) => { const n = [...cards]; n[i] = { ...c, icon: e.target.value }; onChange({ cards: n }); }}>
                {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <input className={inp + ' sm:col-span-3'} value={c.title || ''} onChange={(e) => { const n = [...cards]; n[i] = { ...c, title: e.target.value }; onChange({ cards: n }); }} placeholder="Titolo" />
              <input className={inp + ' sm:col-span-6'} value={c.text || ''} onChange={(e) => { const n = [...cards]; n[i] = { ...c, text: e.target.value }; onChange({ cards: n }); }} placeholder="Descrizione" />
              <button onClick={() => onChange({ cards: cards.filter((_, idx) => idx !== i) })} className="text-white/60 hover:text-red-400 sm:col-span-1"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (t === 'columns') {
    const n = block.columns || 2;
    const contents = block.contents || [];
    return (
      <div className="space-y-2">
        <select className={inp + ' w-32'} value={n} onChange={(e) => onChange({ columns: parseInt(e.target.value, 10) })}>
          <option value={2}>2 colonne</option><option value={3}>3 colonne</option><option value={4}>4 colonne</option>
        </select>
        <div className={`grid gap-2 ${n === 3 ? 'md:grid-cols-3' : n === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'}`}>
          {Array.from({ length: n }).map((_, i) => (
            <textarea key={i} rows={4} className={inp + ' font-mono text-xs'} value={contents[i] || ''} onChange={(e) => { const c = [...contents]; c[i] = e.target.value; onChange({ contents: c }); }} placeholder={`Contenuto colonna ${i + 1}`} />
          ))}
        </div>
      </div>
    );
  }
  if (t === 'accordion') {
    const items = block.items || [];
    return (
      <div className="space-y-2">
        <button onClick={() => onChange({ items: [...items, { title: 'Nuova voce', content: '' }] })} className="bg-navy border border-pink-brand text-pink-brand rounded-full px-3 py-1 text-xs font-bold uppercase inline-flex items-center gap-1"><Plus size={12} /> Voce</button>
        {items.map((it, i) => (
          <div key={i} className="bg-navy border border-white/10 rounded p-2 space-y-1">
            <div className="flex gap-2">
              <input className={inp + ' flex-1'} value={it.title || ''} onChange={(e) => { const n = [...items]; n[i] = { ...it, title: e.target.value }; onChange({ items: n }); }} placeholder="Titolo" />
              <button onClick={() => onChange({ items: items.filter((_, idx) => idx !== i) })} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <textarea rows={3} className={inp + ' font-mono text-xs'} value={it.content || ''} onChange={(e) => { const n = [...items]; n[i] = { ...it, content: e.target.value }; onChange({ items: n }); }} placeholder="Risposta" />
          </div>
        ))}
      </div>
    );
  }
  if (t === 'separator') return <div className="text-white/50 text-xs">Linea di separazione (nessuna opzione)</div>;
  if (t === 'spacer') {
    return <div className="flex items-center gap-2"><label className={lbl + ' !mb-0'}>Altezza px</label><input type="number" className={inp + ' w-32'} value={block.height || 40} onChange={(e) => onChange({ height: parseInt(e.target.value, 10) || 40 })} /></div>;
  }
  if (t === 'html') {
    return <textarea rows={5} className={inp + ' font-mono text-xs'} value={block.code || ''} onChange={(e) => onChange({ code: e.target.value })} placeholder="<div>...</div>" />;
  }
  if (t === 'image') {
    return (
      <div className="space-y-2">
        <UploadField accept="image/*" folder="pages" value={block.src} onChange={(v) => onChange({ src: v })} />
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inp} value={block.alt || ''} onChange={(e) => onChange({ alt: e.target.value })} placeholder="Alt text" />
          <input className={inp} value={block.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} placeholder="Didascalia" />
        </div>
      </div>
    );
  }
  if (t === 'video') return <UploadField accept="video/mp4" folder="pages" value={block.src} onChange={(v) => onChange({ src: v })} />;
  if (t === 'iframe') {
    return (
      <div className="grid sm:grid-cols-3 gap-2">
        <input className={inp + ' sm:col-span-2'} value={block.src || ''} onChange={(e) => onChange({ src: e.target.value })} placeholder="URL embed" />
        <input type="number" className={inp} value={block.height || 480} onChange={(e) => onChange({ height: parseInt(e.target.value, 10) || 480 })} placeholder="Altezza px" />
      </div>
    );
  }
  if (t === 'banner') {
    return (
      <div className="space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inp} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} placeholder="Titolo" />
          <input className={inp} value={block.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} placeholder="Sottotitolo" />
        </div>
        <UploadField label="Immagine sfondo (opzionale)" accept="image/*" folder="banners" value={block.bg} onChange={(v) => onChange({ bg: v })} />
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inp} value={block.cta_label || ''} onChange={(e) => onChange({ cta_label: e.target.value })} placeholder="Pulsante testo" />
          <input className={inp} value={block.cta_url || ''} onChange={(e) => onChange({ cta_url: e.target.value })} placeholder="Pulsante link" />
        </div>
      </div>
    );
  }
  if (t === 'gallery' || t === 'gallery_carousel') {
    const items = block.items || [];
    return (
      <div className="space-y-2">
        <UploadField label="Aggiungi immagine" accept="image/*" folder="gallery" value="" onChange={(v) => { if (v) onChange({ items: [...items, { src: v }] }); }} />
        {t === 'gallery_carousel' && (
          <div className="grid grid-cols-2 gap-2">
            <input className={inp} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} placeholder="Titolo (opzionale)" />
            <select className={inp} value={block.itemsPerView || 3} onChange={(e) => onChange({ itemsPerView: parseInt(e.target.value, 10) })}>
              <option value={2}>2 visibili</option><option value={3}>3 visibili</option><option value={4}>4 visibili</option>
            </select>
          </div>
        )}
        <textarea rows={3} className={inp + ' font-mono text-xs'} value={items.map((i) => i.src).join('\n')} onChange={(e) => onChange({ items: e.target.value.split('\n').filter(Boolean).map((src) => ({ src })) })} placeholder="URL immagini (uno per riga)" />
        {items.length > 0 && (
          <div className="grid grid-cols-6 gap-1">
            {items.map((it, i) => (
              <div key={i} className="relative group">
                <img src={it.src} alt="" className="aspect-square object-cover rounded w-full" />
                <button onClick={() => onChange({ items: items.filter((_, idx) => idx !== i) })} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 text-xs">×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
}

function StylePanel({ block, onChange, onClose }) {
  const s = block.style || {};
  const set = (k, v) => onChange({ style: { ...s, [k]: v } });
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy max-w-lg w-full rounded-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-bold">Stile blocco</h3>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-pink-brand"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Sfondo</label><div className="flex gap-2"><input type="color" value={s.bg || '#001f3f'} onChange={(e) => set('bg', e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/15 bg-navy-dark" /><input value={s.bg || ''} onChange={(e) => set('bg', e.target.value)} className={inp} placeholder="auto" /></div></div>
            <div><label className={lbl}>Testo</label><div className="flex gap-2"><input type="color" value={s.color || '#ffffff'} onChange={(e) => set('color', e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/15 bg-navy-dark" /><input value={s.color || ''} onChange={(e) => set('color', e.target.value)} className={inp} placeholder="auto" /></div></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Allineamento</label><select className={inp} value={s.align || ''} onChange={(e) => set('align', e.target.value)}><option value="">Auto</option><option value="left">Sinistra</option><option value="center">Centro</option><option value="right">Destra</option></select></div>
            <div><label className={lbl}>Padding Y (px)</label><input type="number" className={inp} value={s.paddingY ?? ''} onChange={(e) => set('paddingY', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} /></div>
            <div><label className={lbl}>Padding X (px)</label><input type="number" className={inp} value={s.paddingX ?? ''} onChange={(e) => set('paddingX', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Arrotondamento (px)</label><input type="number" className={inp} value={s.radius ?? ''} onChange={(e) => set('radius', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} /></div>
            <div><label className={lbl}>Larghezza max (px)</label><input type="number" className={inp} value={s.maxWidth ?? ''} onChange={(e) => set('maxWidth', e.target.value === '' ? undefined : parseInt(e.target.value, 10))} /></div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-white/80 text-xs flex items-center gap-2"><input type="checkbox" checked={!!s.hideMobile} onChange={(e) => set('hideMobile', e.target.checked)} /> Nascondi su mobile</label>
            <label className="text-white/80 text-xs flex items-center gap-2"><input type="checkbox" checked={!!s.hideDesktop} onChange={(e) => set('hideDesktop', e.target.checked)} /> Nascondi su desktop</label>
          </div>
          <button onClick={() => onChange({ style: {} })} className="text-pink-brand text-xs font-bold uppercase inline-flex items-center gap-1"><RotateCcw size={12} /> Reset stile</button>
        </div>
      </div>
    </div>
  );
}

function AddBlockModal({ onPick, onClose, insertAt }) {
  const grouped = {
    system: BLOCK_CATALOG.filter((b) => b.category === 'system'),
    content: BLOCK_CATALOG.filter((b) => b.category === 'content'),
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between sticky top-0 bg-navy">
          <div>
            <h3 className="text-white font-bold text-lg">Aggiungi blocco</h3>
            <p className="text-white/50 text-xs">{insertAt == null ? 'In fondo' : `Posizione #${insertAt + 1}`}</p>
          </div>
          <button onClick={onClose} className="p-2 text-white/60 hover:text-pink-brand"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <h4 className="text-pink-brand font-bold text-xs uppercase tracking-widest mb-2">Blocchi ricchi (stile WordPress)</h4>
            <div className="grid sm:grid-cols-3 gap-2">
              {grouped.content.map((b) => {
                const Ic = ICON_MAP[b.icon] || Type;
                return (
                  <button key={b.type} onClick={() => onPick(b.type)} className="text-left p-3 rounded-lg border border-white/10 bg-navy-dark hover:border-pink-brand">
                    <Ic size={18} className="text-pink-brand mb-1" />
                    <div className="text-white font-semibold text-xs">{b.label}</div>
                    <div className="text-white/50 text-[10px]">{b.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <h4 className="text-pink-brand font-bold text-xs uppercase tracking-widest mb-2">Sezioni del sito (system)</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {grouped.system.map((b) => {
                const Ic = ICON_MAP[b.icon] || Type;
                return (
                  <button key={b.type} onClick={() => onPick(b.type)} className="text-left p-3 rounded-lg border border-white/10 bg-navy-dark hover:border-pink-brand flex items-start gap-3">
                    <Ic size={18} className="text-pink-brand mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-white font-semibold text-sm">{b.label}</div>
                      <div className="text-white/50 text-xs">{b.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
